import { useMutation } from '@apollo/client'
import { get, isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import { Input, Button, Stack } from '@collegebacker/backer-ui/ui'
import { useHistory } from 'react-router-dom'

import { Cookie } from '../../../cookie'
import { getQuery } from 'hooks'
import {
  isAuthenticated,
  NumberFormat,
  useVoucherDetails,
  isMobile,
  acceptManagerIfInvited
} from 'utils'
import { ACCEPT_MANAGER_INVITE } from '../signinHelpers'
import config from '../../../config'
import { validateEmail, ErrorMessages } from '../validation'
import { buildPath } from 'routes'

import AuthHeader from './AuthHeader'
import { Head } from 'components/Head'
import { Loading } from 'components/index'
import { ScreenHeader } from 'components/ScreenHeader'
import styles from './Auth.module.scss'

const VERIFICATION_ERROR_MESSAGE = `We're sorry but we couldn't verify this email. Please make sure it is correct or contact us at support@backer.com for help.`

export const GetStarted = ({ verifyEmail }: Props) => {
  const history = useHistory()
  const queryParams = getQuery()
  const notVerified = queryParams.get('email')
  const redirectTo = queryParams.get('redirectTo')
  const [email, setEmail] = useState(notVerified ? notVerified : '')
  const [emailError, setEmailError] = useState(
    notVerified ? VERIFICATION_ERROR_MESSAGE : ''
  )
  const [acceptManagerInvite] = useMutation(ACCEPT_MANAGER_INVITE)

  useEffect(() => {
    if (isAuthenticated()) {
      acceptManagerIfInvited(acceptManagerInvite)
        .then((result) => {
          const didAccept = get(result, 'data.acceptManagerInvite.didAccept')
          history.replace({
            pathname: buildPath('dashboard'),
            state: { acceptedManagerInvite: didAccept }
          })
        })
        .catch(console.error)
    }
  }, [])

  useEffect(() => {
    if (redirectTo) {
      Cookie.set('redirectTo', redirectTo)
    }
  }, [redirectTo])

  if (isAuthenticated()) {
    return <Loading />
  }

  return (
    <>
      <Head title="Sign Up | Backer" />
      <AuthHeader hideHelp={true} />
      <section className={styles.container}>
        <form
          className={styles.form}
          method="post"
          onSubmit={(event) => {
            event.preventDefault()

            const errors: ErrorMessages = validateEmail({ email })
            setEmailError(errors['email'] || '')
            if (isEmpty(errors)) {
              verifyEmail(email)
            }
          }}
        >
          <div>
            <ScreenHeader title="Get started" subtitle={<SignupMessage />} />
            <Input
              autoFocus={!isMobile()}
              label="Enter your email"
              name="email"
              type="email"
              value={email}
              onChange={(event: any) => setEmail(event.target.value)}
              errorMessage={emailError}
              className={styles.input}
            />
          </div>
          <Stack spacing={20}>
            {renderLegalese()}
            <Button
              maxWidth={400}
              type="submit"
              label="Continue"
              disabled={!email.trim()}
            />
          </Stack>
        </form>
      </section>
    </>
  )
}

export const renderLegalese = () => {
  return (
    <p className={styles.legalese}>
      By {isMobile() ? 'tapping' : 'clicking'} "Continue" you agree to our{' '}
      <a
        href={`${config.BACKEND_ROOT_URL}/legal#terms`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Terms of Use
      </a>{' '}
      and{' '}
      <a
        href={`${config.BACKEND_ROOT_URL}/legal#privacy`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Privacy Notice
      </a>
      .
    </p>
  )
}

const SignupMessage = () => {
  const gift = Cookie.get('gift') && JSON.parse(Cookie.get('gift') || '{}')
  const referrerFirstName = Cookie.get('referrerFirstName')
  const referralAmount = parseInt(Cookie.get('referralAmount') || '{}', 10)
  const customMessage = Cookie.get('signupMessage')
  const voucher = Cookie.get('voucher')

  if (gift) {
    return redemptionMessage(gift.amount, gift.sender)
  } else if (referralAmount) {
    return redemptionMessage(referralAmount, referrerFirstName)
  } else if (customMessage) {
    return <>{customMessage}</>
  } else if (voucher) {
    return <VoucherSignupMessage code={voucher} />
  } else {
    return <>Start saving smart together.</>
  }
}

const VoucherSignupMessage = ({ code }: { code: string }) => {
  const { voucher } = useVoucherDetails(code)
  const signupMessage = get(voucher, 'presentation.signupMessage')
  return signupMessage ? <>{signupMessage}</> : null
}

const redemptionMessage = (am: number, sender?: string) => {
  const { currencyFromCents } = NumberFormat
  return (
    <>
      {sender || 'Your friend'}'s {currencyFromCents(am, false)} gift will be
      added to your fund.
    </>
  )
}

export interface Props {
  verifyEmail: (email: string) => void
}
